import React, { useMemo, useState } from "react";
import {
  Box,
  ExpandableSection,
  LineChart,
  MixedLineBarChartProps,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import {
  DlqSizePoint,
  DlqSnapshotPoint,
  Stage,
} from "../../lusca-search-api/generated-src";
import { useDlqSizeHistory } from "../../hooks/useDlqSizeHistory";
import { formatCompactUsd } from "./InvestigationFunnel";

/** Windows offered on the chart; ≤ 14 days is hourly, longer is one point per day. */
export const DLQ_SIZE_RANGES = [
  { id: "7", text: "7 days" },
  { id: "30", text: "30 days" },
  { id: "90", text: "90 days" },
  { id: "365", text: "1 year" },
] as const;

/** What the line measures: the table's item count, or the summed financial impact. */
export type DlqMetric = "items" | "impact";
export const DLQ_METRICS: { id: DlqMetric; text: string }[] = [
  { id: "items", text: "Items" },
  { id: "impact", text: "Impact" },
];

export const DLQ_SIZE_SERIES_TITLE = "Items in the DLQ";
export const DLQ_IMPACT_SERIES_TITLE = "Financial impact";

const formatHour = (d: Date) =>
  d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
const formatDay = (d: Date) =>
  d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

const exactUsd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function toSeries(
  title: string,
  data: { x: Date; y: number }[]
): MixedLineBarChartProps.LineDataSeries<Date>[] {
  const clean = data.filter((d) => !Number.isNaN(d.x.getTime()));
  return clean.length ? [{ title, type: "line", data: clean }] : [];
}

export function dlqSizeSeries(
  points?: DlqSizePoint[] | null
): MixedLineBarChartProps.LineDataSeries<Date>[] {
  return toSeries(
    DLQ_SIZE_SERIES_TITLE,
    (points ?? []).map((p) => ({ x: new Date(p.t), y: p.n }))
  );
}

/** Impact line from the reconcile's per-snapshot totals; snapshots without a figure are skipped. */
export function dlqImpactSeries(
  snapshots?: DlqSnapshotPoint[] | null
): MixedLineBarChartProps.LineDataSeries<Date>[] {
  return toSeries(
    DLQ_IMPACT_SERIES_TITLE,
    (snapshots ?? [])
      .filter((p) => typeof p.impactUsd === "number")
      .map((p) => ({ x: new Date(p.t), y: p.impactUsd as number }))
  );
}

/** Latest minus first value of the window; null with fewer than two values. */
export function seriesChange(values: number[]): number | null {
  if (values.length < 2) return null;
  return values[values.length - 1] - values[0];
}

export function dlqSizeChange(points?: DlqSizePoint[] | null): number | null {
  return seriesChange((points ?? []).map((p) => p.n));
}

export function impactValues(snapshots?: DlqSnapshotPoint[] | null): number[] {
  return (snapshots ?? [])
    .map((p) => p.impactUsd)
    .filter((v): v is number => typeof v === "number");
}

const SIZE_SOURCE_NOTE =
  "Every item in the DLQ table, investigated or not (CloudWatch ItemCount, refreshed every few hours).";
const IMPACT_SOURCE_NOTE =
  "Summed financial impact of every item in the latest hourly DLQ snapshot, investigated or not.";

/** "446,856 items now, +6,856 over 30 days. <source>"; just the source note before data arrives. */
export function describeDlqSize(
  latest: number | null,
  change: number | null,
  rangeLabel: string
): string {
  if (latest === null) return SIZE_SOURCE_NOTE;
  let text = `${latest.toLocaleString()} items now`;
  if (change !== null) {
    const sign = change > 0 ? "+" : "";
    text += `, ${sign}${change.toLocaleString()} over ${rangeLabel}`;
  }
  return `${text}. ${SIZE_SOURCE_NOTE}`;
}

/** "$25.4M at risk now, +$1.2M over 7 days. <source>"; just the source note before data arrives. */
export function describeDlqImpact(
  latest: number | null,
  change: number | null,
  rangeLabel: string
): string {
  if (latest === null) return IMPACT_SOURCE_NOTE;
  let text = `${formatCompactUsd(latest)} at risk now`;
  if (change !== null) {
    let sign = "";
    if (change > 0) sign = "+";
    else if (change < 0) sign = "−";
    text += `, ${sign}${formatCompactUsd(Math.abs(change))} over ${rangeLabel}`;
  }
  return `${text}. ${IMPACT_SOURCE_NOTE}`;
}

function chartStatus(
  failed: boolean,
  loading: boolean
): "error" | "loading" | "finished" {
  if (failed) return "error";
  return loading ? "loading" : "finished";
}

/**
 * Whole-DLQ size over time on the home page. Items = the DLQ table's CloudWatch ItemCount (moves
 * in steps, DynamoDB refreshes it every few hours). Impact = the summed financial impact of each
 * hourly DLQ snapshot, recorded by the reconcile, so it lags the live table by the Glue run and
 * starts from the first recorded snapshot.
 */
export const DlqSizeChart: React.FC<{ stage: Stage }> = ({ stage }) => {
  const [days, setDays] = useState<string>("30");
  const [metric, setMetric] = useState<DlqMetric>("items");
  const history = useDlqSizeHistory(stage, Number(days));
  const points = history.data?.points;
  const snapshots = history.data?.snapshots;
  const series = useMemo(
    () =>
      metric === "items" ? dlqSizeSeries(points) : dlqImpactSeries(snapshots),
    [metric, points, snapshots]
  );
  const hourly = (history.data?.periodSeconds ?? 3600) < 86400;
  const formatTime = hourly ? formatHour : formatDay;
  const rangeLabel =
    DLQ_SIZE_RANGES.find((r) => r.id === days)?.text ?? `${days} days`;

  let description: string;
  if (metric === "items") {
    const latest = points?.length ? points[points.length - 1].n : null;
    description = describeDlqSize(latest, dlqSizeChange(points), rangeLabel);
  } else {
    const values = impactValues(snapshots);
    const latest = values.length ? values[values.length - 1] : null;
    description = describeDlqImpact(latest, seriesChange(values), rangeLabel);
  }
  const isImpact = metric === "impact";
  const formatValue = (y: number) =>
    isImpact ? exactUsd.format(y) : y.toLocaleString();
  let xTitle = "Snapshot";
  if (!isImpact) xTitle = hourly ? "Hour" : "Day";

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded
      headerText="DLQ size over time"
      headerDescription={description}
      headerActions={
        <SpaceBetween direction="horizontal" size="xs">
          <SegmentedControl
            selectedId={metric}
            onChange={({ detail }) => setMetric(detail.selectedId as DlqMetric)}
            label="Measure"
            options={DLQ_METRICS}
          />
          <SegmentedControl
            selectedId={days}
            onChange={({ detail }) => setDays(detail.selectedId)}
            label="Time range"
            options={DLQ_SIZE_RANGES.map((r) => ({ id: r.id, text: r.text }))}
          />
        </SpaceBetween>
      }
    >
      <LineChart<Date>
        series={series}
        xScaleType="time"
        height={180}
        hideFilter
        hideLegend
        statusType={chartStatus(!!history.error, history.isLoading)}
        errorText={history.error?.message ?? "Could not load the DLQ size"}
        loadingText="Loading DLQ size"
        ariaLabel={
          isImpact
            ? "Financial impact of the DLQ over time"
            : "Items in the DLQ over time"
        }
        xTitle={xTitle}
        yTitle={isImpact ? "Impact (USD)" : "Items"}
        xTickFormatter={formatTime}
        yTickFormatter={isImpact ? formatCompactUsd : undefined}
        i18nStrings={{
          xTickFormatter: formatTime,
          yTickFormatter: isImpact ? formatCompactUsd : undefined,
          detailPopoverDismissAriaLabel: "Dismiss",
          legendAriaLabel: "Legend",
          chartAriaRoleDescription: "line chart",
        }}
        detailPopoverSeriesContent={({ series: s, y }) => ({
          key: s.title,
          value: typeof y === "number" ? formatValue(y) : "",
        })}
        empty={
          <Box textAlign="center" color="inherit">
            <StatusIndicator type="info">
              {isImpact
                ? "No impact recorded for this range yet; it is recorded from each hourly DLQ snapshot."
                : "No DLQ size recorded for this range."}
            </StatusIndicator>
          </Box>
        }
      />
    </ExpandableSection>
  );
};

export default DlqSizeChart;
